import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { isEqual } from 'lodash';
import { selectRfi, setElementValues } from 'store/rfiSlice';
import RfiElement from 'types/RfiElement';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { answerValueTypes, Element, elementTypes } from '../../types/RfiTypes';

export interface DateTimeElementProps {
  element: Element;
  readOnly?: boolean;
  questionNumber?: number;
  showValue?: boolean;
  designMode?: boolean;
}

const DateTime = (props: any) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(advancedFormat);

  // Hooks
  const rfi = useAppSelector(selectRfi);
  const dispatch = useAppDispatch();

  // State
  const timeZoneElement = rfi.finalize?.find((e: RfiElement) => {
    return e.mapsToField === "RESPONSES_DUE_TZ";
  });
  const timeZone =
    timeZoneElement &&
    timeZoneElement.answers &&
    timeZoneElement.answers[0]?.value
      ? timeZoneElement.answers[0]?.value
      : undefined;
  const [value, setValue] = useState<Dayjs | null>(() => {
    if (props.element.answers?.length ?? 0 > 0) {
      if (props.element.answers[0]?.value) {
        return dayjs.utc(props.element.answers[0]?.value);
      }
    }
    return null;
  });

  // Handlers
  const handleChange = (event: Dayjs | null) => {
    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: [
          {
            value: event?.isValid() ? event?.toISOString() : "",
            answerValueType: answerValueTypes.DEFAULT,
          },
        ],
      })
    );
    setValue(event?.isValid() ? event : null);
  };

  // Render
  if (props.element.elementType === elementTypes.FIELD) {
    if (props.readOnly) {
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            {props.element.fieldLabel}
          </div>
          <div className="col-lg-8 fieldElement">
            {value
              ? dayjs
                  .tz(
                    value.format("YYYY-MM-DDTHH:mm:ss"),
                    timeZone ?? "America/New_York"
                  )
                  .format("lll z")
              : ""}
            {/* {value?.tz(timeZone?? "America/New_York").format("lll z")?? ""} */}
          </div>
        </div>
      );
    } else
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            <label
              className="col-form-label"
              htmlFor={`datepicker-${props.element.elementId}`}
            >
              {props.element.fieldLabel}{" "}
              {props.overrideRequired === undefined ? (
                props.element.requiredResponse ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )
              ) : props.overrideRequired === true ? (
                <span className="requiredField">(required)</span>
              ) : (
                ""
              )}
            </label>
          </div>
          <div className="col-lg-8 fieldElement">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                slotProps={{
                  textField: {
                    id: `datetimepicker-${props.element.elementId}`,
                  },
                }}
                timezone="UTC"
                value={value}
                onChange={handleChange}
              />
            </LocalizationProvider>
            {/* <DateTimePicker 
            id={`datepicker-${props.element.elementId}`}
            value={(value)} onChange={handleChange} fillMode="solid" size="small" width={300} /> */}
          </div>
        </div>
      );
  } else {
    if (props.readOnly) {
      return (
        <>
          <div className="col-form-label">
            {`${props.questionNumber}.`} {props.element.fieldLabel}
          </div>
          <div>
            {/* {props.showValue ? value?.toString() : ""} */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                slotProps={{
                  textField: {
                    id: `datetimepicker-${props.element.elementId}`,
                  },
                }}
                value={value}
                onChange={handleChange}
                disabled={true}
                timezone="UTC"
              />
            </LocalizationProvider>
          </div>
        </>
      );
    } else
      return (
        <>
          <fieldset>
            <legend className="visuallyHidden">
              {props.element.fieldLabel}
            </legend>
            <label
              className="col-form-label"
              htmlFor={`datepicker-${props.element.elementId}`}
            >
              <div>
                {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </div>
            </label>
            <div>
              {/* <DateTimePicker 
              id={`datepicker-${props.element.elementId}`}
              value={(value)} onChange={handleChange} fillMode="solid" size="small" width={300} /> */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  slotProps={{
                    textField: {
                      id: `datetimepicker-${props.element.elementId}`,
                    },
                  }}
                  value={value}
                  onChange={handleChange}
                  timezone="UTC"
                />
              </LocalizationProvider>
            </div>
          </fieldset>
        </>
      );
  }
};

const areEqual = (prevProps: any, nextProps: any) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element.elementId, nextProps.element.elementId) &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const DateTimeElement = React.memo(DateTime, areEqual);

export default DateTimeElement;
